/**
 * Rodeo Wizard — Multi-step creation/edit page
 *
 * Steps: BasicInfo → EventConfigs → Performances → Review
 *
 * Accessible via:
 *   /delegate/rodeos/create                              (new)
 *   /delegate/rodeos/:rodeoId/edit                       (edit existing)
 *   /delegate/rodeos/:rodeoId/edit?step=N                (deep-link to step N, 1-4)
 *   /delegate/rodeos/:rodeoId/edit?step=2&performanceId=X&eventId=Y
 *     (deep-link to Event Configs step with a specific performance/event in context)
 *
 * Deep-link query params:
 *   step          — Jump to wizard step N (1-4) after the draft finishes loading.
 *                   The step override is applied in the loadDraft() Promise .then()
 *                   callback so it always runs AFTER loadDraft resets currentStep→1.
 *   performanceId — Contextual hint identifying which performance to focus on arrival.
 *                   Stored for downstream sub-selection UX (not yet wired to store).
 *   eventId       — Contextual hint identifying which event to focus on arrival.
 *                   Stored for downstream sub-selection UX (not yet wired to store).
 *
 * Host layout uses the shared <WizardShell /> component (same pattern as
 * JackpotWizard). This file owns:
 *   - Route params / navigation
 *   - Zustand store wiring (load/reset/save/finalize)
 *   - Toast notifications
 *   - Loading skeleton
 *
 * All wizard chrome (header with title/X, left nav, expand-all toggle, footer)
 * is delegated to <WizardShell />. Visual layout mirrors the "Add Event" modal:
 * X in top-right, Previous/Next bottom-left, Save Draft + Cancel + Save &
 * Publish bottom-right, full rounded border.
 */

import { useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { WizardShell, type WizardStepDef } from '@/components/wizard';
import { useToast } from '@/contexts/ToastContext';
import { rodeoOpsKeys } from '@/api/extensions/useRodeoOps';
import { jackpotOpsKeys } from '@/api/extensions/useJackpotOps';
import { registrationOpsKeys } from '@/api/extensions/useRegistrationOps';
import { eventsKeys } from '@/api/generated_hooks/generated-events-hooks';
import { performancesKeys } from '@/api/generated_hooks/generated-performances-hooks';
import { performanceEventsKeys } from '@/api/generated_hooks/generated-performance_events-hooks';
import { rodeoRoundConfigsKeys } from '@/api/generated_hooks/generated-rodeo_round_configs-hooks';
import {
  useRodeoStore,
  type RodeoWizardStep,
} from '@/state_management_and_stores/rodeo.store';
import { BasicInfoStep } from '@/components/rodeo-wizard/BasicInfoStep';
import { PerformanceConfigStep } from '@/components/rodeo-wizard/PerformanceConfigStep';
import { EventConfigStep } from '@/components/rodeo-wizard/EventConfigStep';
import { ReviewStep } from '@/components/rodeo-wizard/ReviewStep';

/**
 * Ordered step labels shown in the WizardShell left nav.
 * Index 0 = step 1 (Basic Info). Order matches RodeoWizardStep values in the store.
 */
const RODEO_STEPS: WizardStepDef[] = [
  { label: 'Basic Info' },
  { label: 'Events' },
  { label: 'Performances' },
  { label: 'Review' },
];

export default function RodeoWizard() {
  const { rodeoId } = useParams<{ rodeoId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const currentStep = useRodeoStore((s) => s.currentStep);
  const isSaving = useRodeoStore((s) => s.isSaving);
  const saveError = useRodeoStore((s) => s.saveError);
  const isLoading = useRodeoStore((s) => s.isLoading);
  const basicInfo = useRodeoStore((s) => s.basicInfo);
  const {
    setStep,
    nextStep,
    prevStep,
    loadDraft,
    saveDraft,
    resetWizard,
  } = useRodeoStore();

  // ── Parse deep-link params from current searchParams on every render ─────────
  // The ref is refreshed on each render so that if the component stays mounted
  // and the user navigates to a different /delegate/rodeos/:rodeoId/edit?...
  // URL (same route, new rodeoId or new params), the effect reads the NEW query
  // params — not the stale ones from the first render.
  //
  // Supported params:
  //   ?step=N           — jump to wizard step N (1-4) after draft loads
  //   ?performanceId=X  — hint: which performance is in context (future sub-selection)
  //   ?eventId=Y        — hint: which event is in context (future sub-selection)
  const deepLinkRef = useRef<{
    step: RodeoWizardStep | null;
    performanceId: string | null;
    eventId: string | null;
  }>({ step: null, performanceId: null, eventId: null });

  // Always re-derive from the current searchParams so stale values are never used.
  const _stepStr = searchParams.get('step');
  const _stepNum = _stepStr ? Number(_stepStr) : NaN;
  deepLinkRef.current = {
    step: Number.isInteger(_stepNum) && _stepNum >= 1 && _stepNum <= 4
      ? (_stepNum as RodeoWizardStep)
      : null,
    performanceId: searchParams.get('performanceId'),
    eventId: searchParams.get('eventId'),
  };

  // ── Load draft + apply deep-link ────────────────────────────────────────────
  // The step override is applied in the loadDraft() Promise .then() callback so
  // it is GUARANTEED to run after loadDraft resets currentStep→1.  Tracking
  // isLoading transitions would be fragile (React 18 can batch the
  // isLoading:true → isLoading:false Zustand updates, meaning the component
  // may never re-render in the intermediate true state, so the latch never
  // arms).  The Promise approach is simpler and unambiguously correct.
  useEffect(() => {
    let cancelled = false;

    const applyDeepLinkStep = () => {
      if (cancelled) return;
      const { step } = deepLinkRef.current!;
      if (step !== null) {
        setStep(step);
      }
    };

    if (rodeoId) {
      // loadDraft is typed Promise<void>; always call .then() to apply step
      // after the draft (and its currentStep:1 reset) has fully settled.
      void loadDraft(rodeoId).then(applyDeepLinkStep);
    } else {
      resetWizard();
      // In create mode resetWizard() is synchronous, so we can apply the
      // deep-link step immediately.
      applyDeepLinkStep();
    }

    return () => {
      // Prevent stale callbacks from firing if rodeoId changes or component
      // unmounts (important for React 18 StrictMode double-invoke).
      cancelled = true;
    };
  }, [rodeoId, loadDraft, resetWizard, setStep]);

  /**
   * 1-based step indices that are missing required data. Used to annotate the
   * left nav and gate Save Draft / Save & Publish. Next/Previous and step
   * clicks are never blocked — users can move around freely.
   *
   * Step 1 requires name + organization + start date. Other steps have no
   * hard-required inputs today (Performances step recommends ≥1 but the
   * backend only enforces that at finalize).
   */
  const missingSteps = new Set<number>();
  if (
    !basicInfo.name.trim() ||
    !basicInfo.organization_id ||
    !basicInfo.start_date ||
    !basicInfo.entry_close_time
  ) {
    missingSteps.add(1);
  }
  const hasMissing = missingSteps.size > 0;

  const handleSave = async () => {
    try {
      await saveDraft();
      // Invalidate every cache the My-Rodeos dropdown reads from. Without
      // these, edits made in the wizard (perf assignments, event tweaks,
      // round configs) don't surface in the dropdown view until the user
      // hard-refreshes the page.
      queryClient.invalidateQueries({ queryKey: rodeoOpsKeys.all });
      queryClient.invalidateQueries({ queryKey: jackpotOpsKeys.all });
      queryClient.invalidateQueries({ queryKey: eventsKeys.all });
      queryClient.invalidateQueries({ queryKey: performancesKeys.all });
      queryClient.invalidateQueries({ queryKey: performanceEventsKeys.all });
      queryClient.invalidateQueries({ queryKey: rodeoRoundConfigsKeys.all });
      // Registration policy (cash allowed, surcharge, entry-close time,
      // preference config, fees-due message) is derived from the rodeo on
      // the backend. Without this, edits to allow_cash_payment etc. don't
      // appear in the registration modal until a hard page refresh.
      queryClient.invalidateQueries({ queryKey: registrationOpsKeys.all });
      // saveDraft now carries `is_published` as part of the basic-info
      // payload, so the single Save button both persists the draft and
      // flips publish state as needed. The toast reflects whichever
      // state the user toggled to on the Review step.
      const { isPublished } = useRodeoStore.getState();
      showSuccess(isPublished ? 'Rodeo saved (published)' : 'Rodeo saved (draft)');
    } catch {
      const { saveError: err } = useRodeoStore.getState();
      showError('Save failed', err ?? 'Unknown error');
    }
  };

  const handleClose = () => navigate('/delegate/rodeos');

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6 space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }

  // Single Save button handles both draft saves and publish/unpublish.
  // The Review step carries a Publish toggle that rides along with the
  // save; there is no longer a separate "Save & Publish" primary button.
  const secondaryAction = (
    <Button
      onClick={handleSave}
      disabled={isSaving || hasMissing}
      title={hasMissing ? 'Resolve the missing fields before saving.' : undefined}
    >
      {isSaving ? 'Saving...' : 'Save'}
    </Button>
  );

  const primaryAction = null;

  return (
    <WizardShell
      title={rodeoId ? 'Edit Rodeo' : 'Create Rodeo'}
      onClose={handleClose}
      steps={RODEO_STEPS}
      currentStep={currentStep}
      onStepChange={(step) => setStep(step as RodeoWizardStep)}
      onNext={nextStep}
      onBack={prevStep}
      invalidSteps={missingSteps}
      // Next/Previous never block on validation — users may click any step.
      // Save buttons (secondaryAction/primaryAction) handle gating.
      secondaryAction={secondaryAction}
      primaryAction={primaryAction}
      belowContent={
        saveError ? (
          <p className="text-sm text-destructive">{saveError}</p>
        ) : null
      }
      allStepContent={[
        <BasicInfoStep key="step-1-basic-info" />,
        <EventConfigStep key="step-2-event-configs" />,
        <PerformanceConfigStep key="step-3-performances" />,
        <ReviewStep key="step-4-review" />,
      ]}
      className="min-h-[85vh]"
    >
      {currentStep === 1 && <BasicInfoStep />}
      {currentStep === 2 && <EventConfigStep />}
      {currentStep === 3 && <PerformanceConfigStep />}
      {currentStep === 4 && <ReviewStep />}
    </WizardShell>
  );
}
